import { useEffect, useMemo, useState } from "react";
import { GeoLocationApiClient } from "../api/apiClientGeoLocation";
import { CherryPayApi } from "../api/models";
import { useApiClient } from "./useApiClient";
import { useToast } from "./useToast";

export interface UseGeoLocationsResult {
  locations: CherryPayApi.GeoLocation[];
  isLoading: boolean;
  hasFetched: boolean;
}

export const useGeoLocations = (businessId: string): UseGeoLocationsResult => {
  const apiClient = useApiClient();
  const { showErrorToast } = useToast();
  const geoLocationApiClient = useMemo(
    () => new GeoLocationApiClient(apiClient.cherryPayClient),
    [apiClient]
  );

  const [locations, setLocations] = useState<CherryPayApi.GeoLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    geoLocationApiClient
      .getGeoLocations(businessId)
      .then((result) => {
        if (cancelled) return;
        if (result.ok) {
          setLocations(result.data ?? []);
          setHasFetched(true);
        } else {
          showErrorToast(result.message || "Failed to load geo-locations");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [geoLocationApiClient, businessId, showErrorToast]);

  return { locations, isLoading, hasFetched };
};
